import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Button, Dropdown, Menu, Layout, Modal } from "antd";
import { IoPerson } from "react-icons/io5";
import { FiMessageSquare } from "react-icons/fi";
import { DownOutlined, MenuOutlined, CloseOutlined } from "@ant-design/icons";

import { BsSun, BsMoon } from "react-icons/bs";
import logo from "../asset/logo.png";
import logowhite from "../asset/logowhite.png";
import SummaryApi from "../common";
import { useAuth } from "../helpers/AuthContext";

const StudentHeader = ({ onToggleSidebar, isSidebarOpen }) => {

  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const { studentId, studentName, setStudentName, logout } = useAuth();

  /*** Theme handling ***/
  const [isDark, setIsDark] = useState(false);

  // Apply theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    const shouldBeDark = savedTheme === "dark";
    setIsDark(shouldBeDark);
    
    if (shouldBeDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, []);

  // Listen for theme changes
  useEffect(() => {
    const handleThemeChange = () => {
      const isCurrentlyDark = document.documentElement.classList.contains("dark");
      setIsDark(isCurrentlyDark);
    };

    window.addEventListener("themeChanged", handleThemeChange);
    const handleStorageChange = (e) => {
      if (e.key === "theme") {
        handleThemeChange();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("themeChanged", handleThemeChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    
    if (newIsDark) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
    window.dispatchEvent(new Event("themeChanged"));
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch student details
  useEffect(() => {
    if (studentId && !studentName) {
      fetch(SummaryApi.getStudentById.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ student_id: studentId }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status && data.data?.first_name) {
            setStudentName(data.data.first_name);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch student details:", err);
          logout();
        });
    } else if (!studentId && studentName) {
      logout();
    }
  }, [studentId, studentName, setStudentName, logout]);

  const handleLogoutConfirm = () => {
    Modal.confirm({
      title: "Confirm Logout",
      content: "Are you sure you want to log out?",
      okText: "Logout",
      cancelText: "Cancel",
      className: isDark ? "dark-theme-modal" : "", 
      okButtonProps: {
        className: isDark 
          ? "bg-[#1f4774] border-[#1f4774] hover:bg-blue-600 hover:border-blue-600 text-white" 
          : "bg-[#1f4774] border-[#1f4774] hover:bg-blue-700 hover:border-blue-700 text-white"
      },
      cancelButtonProps: {
        className: isDark 
          ? "bg-red-900 border-red-900 hover:bg-red-800 hover:border-red-800 text-white"
          : "bg-red-600 border-red-600 hover:bg-red-500 hover:border-red-500 text-white"
      },
      onOk() {
        logout();
        navigate("/student-login");
      },
      onCancel() {
        console.log("Logout cancelled");
      },
    });
  };

  const handleProfileClick = () => {
    navigate("/student-panel/profile");
  };

  const userMenu = (
    <Menu
      theme={isDark ? "dark" : "light"}
      style={{
        backgroundColor: isDark ? "#1f2937" : "#ffffff", 
        border: isDark ? "1px solid #374151" : "none",   
      }}
    >
      <Menu.Item 
        key="1" 
        onClick={handleProfileClick}
        style={{ color: isDark ? "#ffffff" : "inherit" }} 
      >
        Profile
      </Menu.Item>
      <Menu.Item 
        key="2" 
        onClick={handleLogoutConfirm}
        style={{ color: isDark ? "#ffffff" : "inherit" }} 
      >
        Logout
      </Menu.Item>
    </Menu>
  );

  return (
    <Layout>
      <header
        className={`sticky top-0 z-50 shadow-sm transition-colors duration-300 ${
          isDark 
            ? "bg-gray-900 border-gray-700 text-white" 
            : "bg-white border-gray-200 text-gray-900"
        } border-b`}
        style={{
          height: "64px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 16px",
        }}
      >
        {/* Left Section - Logo and Sidebar Toggle */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {!isMobile && (
            <Button
              type="text"
              // 1. Added style prop to icon for explicit color control
              icon={<MenuOutlined style={{ color: isDark ? '#ffffff' : 'inherit' }} />} 
              onClick={onToggleSidebar}
              // 2. Changed className to text-white for dark mode
              className={isDark ? "text-white hover:text-gray-300" : "text-gray-800"} 
            />
          )}

          <div onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
            <img
              src={logo}
              alt="Skillara Logo"
              className="object-contain transition-all duration-300 h-10 sm:h-12 block dark:hidden"
            />
            <img
              src={logowhite}
              alt="Skillara Logo"
              className="object-contain transition-all duration-300 h-10 sm:h-12 hidden dark:block"
            />
          </div>
        </div>

        {/* Right Section - Navigation + Theme Toggle + Profile */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {!isMobile && (
            <Button
              type="link"
              className={`font-semibold transition-colors duration-300 ${
                isDark 
                  ? "text-blue-300 hover:text-blue-200" 
                  : "text-blue-900 hover:text-blue-700"
              }`}
              onClick={() => navigate("/all-courses")}
              style={{ fontWeight: 600 }}
            >
              Explore Courses
            </Button>
          )}

          <Button
            type="text"
            onClick={toggleTheme}
            className={`transition-colors duration-300 ${
              isDark ? "hover:bg-gray-800 text-yellow-400" : "hover:bg-gray-100 text-gray-600"
            }`}
          >
            {isDark ? (
              <BsSun style={{ fontSize: "18px" }} />
            ) : (
              <BsMoon style={{ fontSize: "18px" }} />
            )}
          </Button>

          <Dropdown overlay={userMenu} trigger={["click"]}>
            <Button
              type="text"
              className={`transition-colors duration-300 ${
                isDark ? "hover:bg-gray-800" : "hover:bg-gray-100"
              }`}
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <div
                className={`transition-colors duration-300 ${
                  isDark ? "bg-blue-600" : "bg-blue-700"
                }`}
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "#fff",
                  fontWeight: "bold",
                }}
              >
                <IoPerson size={18} />
              </div>
              <span className={`transition-colors duration-300 ${
                isDark ? "text-gray-200" : "text-blue-900"
              }`} style={{ fontWeight: 600 }}>
                {studentName || "Student"}
                {!isMobile && (
                  <span className="text-[10px] ml-1">
                    <DownOutlined className={isDark ? "text-gray-200" : "text-blue-900"} />
                  </span>
                )}
              </span>
            </Button>
          </Dropdown>
        </div>
      </header>
    </Layout>
  );
};

export default StudentHeader;